import React, {
  MutableRefObject,
  ReactText,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Table as AntdTable } from "antd";
import InfiniteScroll from "react-infinite-scroller";
import {
  ColumnsType,
  Key,
  SorterResult,
  TablePaginationConfig,
} from "antd/lib/table/interface";
// @ts-ignore
import ReactDragListView from "react-drag-listview";
import {
  NOT_DRAGGABLE_CLASS_NAME,
  ROW_KEY,
  SCROLL_SIZE,
} from "../../../constants/table";
import ResizableTitle from "../../tables/ResizableTitle";
import { IObjectMap } from "../../../interfaces/common";
import {
  DEFAULT_SORT_DIRECTION,
  DEFAULT_SORT_FIELD,
} from "../../../constants/sorting";
import SettingsStore from "../../../stores/SettingsStore";

interface Props<T> {
  readonly data: Object[];
  readonly onRowSelectors?: (items: ReactText[]) => void;
  readonly onExpandedRowsChange?: (items: ReactText[]) => void;
  readonly expandedRows?: ReactText[];
  readonly hasMoreItems?: boolean;
  readonly settingsStore?: SettingsStore;
  readonly onLoad: (parameters?: IObjectMap, forceUpdate?: boolean) => void;
  readonly isLoading?: boolean;
  readonly selectedRows?: ReactText[];
  readonly checkStrictly?: boolean;
  readonly expandableConfig?: Object;
  readonly columns: ColumnsType<T>;
  readonly hideSelectAll?: boolean;
  readonly getCheckboxProps?: (record: Object) => Object;
  readonly defaultExpandAllRows?: boolean;
  readonly rowClassName?: (
    record: Object,
    index: number,
    indent: number
  ) => string;
}

const updateHeight = (ref: MutableRefObject<HTMLDivElement | null>) => {
  if (!ref.current) {
    return;
  }
  const { top } = ref.current.getBoundingClientRect();
  const { innerHeight } = window;

  ref.current.style.maxHeight = `${innerHeight - top - 10}px`;
};

const defaultSorter = {
  field: DEFAULT_SORT_FIELD,
  order: DEFAULT_SORT_DIRECTION,
};

const WAIT_TIME_TO_SCROLL = 10;

const Table = <T extends object>({
  data,
  onRowSelectors,
  settingsStore,
  onLoad,
  selectedRows,
  rowClassName,
  isLoading = false,
  hasMoreItems = false,
  checkStrictly = true,
  expandableConfig = {},
  onExpandedRowsChange,
  expandedRows,
  columns,
  hideSelectAll = false,
  getCheckboxProps,
  defaultExpandAllRows = false,
}: Props<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.addEventListener("resize", () => updateHeight(scrollRef));
    updateHeight(scrollRef);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      if (isLoading) {
        return;
      }

      if (!settingsStore) {
        return;
      }

      settingsStore.tableScrollPosition =
        (event.target as HTMLDivElement).scrollLeft || 0;
    };

    const { current } = scrollRef;

    if (current) {
      current.addEventListener("scroll", handler);
    }

    return () => {
      if (current) {
        current.removeEventListener("scroll", handler);
      }
    };
  }, [isLoading, settingsStore]);

  useEffect(() => {
    const { current } = scrollRef;

    if (!isLoading && current && settingsStore) {
      setTimeout(() => {
        current.scrollLeft = settingsStore.scrollPosition;
      }, WAIT_TIME_TO_SCROLL);
    }
  }, [settingsStore, isLoading]);

  useEffect(() => {
    if (
      !isLoading &&
      hasMoreItems &&
      scrollRef.current &&
      scrollRef.current.scrollHeight <= scrollRef.current.clientHeight
    ) {
      onLoad();
    }
  }, [isLoading, hasMoreItems, onLoad]);

  const components = settingsStore?.isEnabledReorder
    ? undefined
    : {
        header: {
          cell: ResizableTitle,
        },
      };

  const onSelectRowsHandler = useCallback(
    (ids: React.ReactText[]) => {
      if (onRowSelectors) {
        onRowSelectors(ids);
      }
    },
    [onRowSelectors]
  );

  const getRowSelection = useCallback(() => {
    if (onRowSelectors) {
      return {
        selectedRowKeys: selectedRows,
        onChange: onSelectRowsHandler,
        hideSelectAll: hideSelectAll,
        getCheckboxProps: getCheckboxProps,
        checkStrictly,
      };
    }

    return undefined;
  }, [
    checkStrictly,
    onRowSelectors,
    onSelectRowsHandler,
    selectedRows,
    hideSelectAll,
    getCheckboxProps,
  ]);

  const onTableChangeHandler = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, (Key | boolean)[] | null>,
      sorter: SorterResult<Object> | SorterResult<Object>[]
    ): void => {
      onLoad(
        {
          page: pagination.current,
          sortColumn: Array.isArray(sorter)
            ? defaultSorter.field
            : sorter.field,
          sortDirection: Array.isArray(sorter)
            ? defaultSorter.order
            : sorter.order,
          grouping: filters,
        },
        true
      );
    },
    [onLoad]
  );

  const table = (
    <AntdTable
      expandable={expandableConfig}
      onExpandedRowsChange={onExpandedRowsChange}
      expandedRowKeys={expandedRows}
      rowSelection={getRowSelection()}
      rowKey={ROW_KEY}
      rowClassName={rowClassName}
      columns={columns as ColumnsType<object>}
      pagination={false}
      dataSource={data}
      onChange={onTableChangeHandler}
      scroll={{
        scrollToFirstRowOnChange: false,
        ...(settingsStore?.isEnabledScrollbar ? { x: SCROLL_SIZE } : {}),
      }}
      loading={isLoading}
      components={components}
      bordered
      sticky
      defaultExpandAllRows={defaultExpandAllRows}
    />
  );

  const loadMoreHandler = useCallback(() => {
    onLoad();
  }, [onLoad]);

  const onDragEnd = (fromIndex: number, toIndex: number) => {
    settingsStore?.onColumnDragEndHandler(
      fromIndex,
      toIndex,
      onRowSelectors ? 1 : 0
    );
  };

  const reactDragListView = (
    <ReactDragListView
      nodeSelector="th:not(.ant-table-selection-column)"
      onDragEnd={onDragEnd}
      ignoreSelector={`th.${NOT_DRAGGABLE_CLASS_NAME}`}
      enableScroll={false}
    >
      {table}
    </ReactDragListView>
  );

  return (
    <div className="scroll-wrapper" ref={scrollRef}>
      <InfiniteScroll
        initialLoad={false}
        loadMore={loadMoreHandler}
        hasMore={hasMoreItems}
        useWindow={false}
        getScrollParent={() => scrollRef.current}
      >
        {settingsStore?.isEnabledReorder ? reactDragListView : table}
      </InfiniteScroll>
    </div>
  );
};

export default Table;
